
import Vue from "vue";
import { ListSupplierEvent, SupplierEventProductName, UISupplierEventPublishedState, UISupplierEventStatus, UISupplierEventType } from "@/features/SupplierEvents/SupplierEvent";
import { DataTableOptions, PagingParameter } from "@/shared/models/PagingParameter";
import { formatDateWithTimestamp } from "@/shared/utils/common_utils";
import FileList, { FileListDocumentType } from "@/features/Documents/FileList.vue";
import { SupplierEventResponse } from "@/features/SupplierEvents/SupplierEventService";
import AppError from "@/shared/components/AppError.vue";

export default Vue.extend({
    name: "SupplierEventList",
    components: {
        AppError,
        FileList,
    },
    props: {
        // TODO #3738210: Include date range props. Watch for when they change to fetch scorecards with date range filters.
        supplierID: {
            type: String,
            required: false,
            default: "",
        },
        supplierEventListOptions: {
            default: {
                sortBy: ["createdTimestampUTC"],
                sortDesc: [true],
                itemsPerPage: 10,
                page: 1,
            } as DataTableOptions,
            required: true,
        },
        beforeDate: {
            default: new Date().toISOString(),
            required: true,
        },
        afterDate: {
            default: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString(), // one year ago today
            required: true,
        },
    },
    data: () => ({
        headers: [
            {
                text: "ID",
                value: "id",
                sortable: true,
                align: "start",
            },
            {
                text: "Created Date",
                value: "createdTimestampUTC",
                sortable: true,
                align: "start",
            },
            {
                text: "Submitter",
                value: "submitterName",
                sortable: true,
                align: "start",
            },
            {
                text: "Product(s)",
                value: "products",
                sortable: true,
                align: "start",
            },
            {
                text: "Type",
                value: "type",
                sortable: true,
                align: "start",
            },
            {
                text: "Status",
                value: "status",
                sortable: true,
                align: "start",
            },
            {
                text: "Publication Status",
                value: "publishedState",
                sortable: true,
                align: "start",
            },
            {
                text: "Files",
                value: "documentMetadata",
                sortable: false,
                align: "start",
            },
        ],
        supplierEventStatus: UISupplierEventStatus,
        supplierEventType: UISupplierEventType,
        supplierEventPublishedState: UISupplierEventPublishedState,
        FileListDocumentType: FileListDocumentType,
        supplierEvents: [] as ListSupplierEvent[],
        totalSupplierEvents: 0,
        loading: true,
        errorMessage: "",
        showError: false,
    }),
    watch: {
        supplierEventListOptions: {
            handler() {
                this.getSupplierEvents();
            },
            deep: true,
        },
        supplierID() {
            this.getSupplierEvents();
        },
        afterDate(newValue, oldValue) {
            // Only re-fetch if the days have changed; disregard any changes to the time
            if (new Date(newValue).toDateString() != new Date(oldValue).toDateString()) {
                this.getSupplierEvents();
            }
        },
        beforeDate(newValue, oldValue) {
            // Only re-fetch if the days have changed; disregard any changes to the time
            if (new Date(newValue).toDateString() != new Date(oldValue).toDateString()) {
                this.getSupplierEvents();
            }
        },
    },
    created: function () {
        this.getSupplierEvents();
    },
    methods: {
        formatDateWithTimestampForTable(dateString: string): string {
            const date = new Date(dateString);
            return formatDateWithTimestamp(date);
        },
        formatProductsForTable(products: SupplierEventProductName[]): string {
            return products.map(p => (p.supplierProductName ? `${p.supplierProductName} (${p.name})` : p.name)).join(", ");
        },
        boldUnreadSupplierEvents(item: ListSupplierEvent): string {
            if (item.read) {
                return "";
            }
            return "boldRow";
        },
        async getSupplierEvents(): Promise<void> {
            try {
                if (this.supplierID) {
                    this.loading = true;
                    const pagingParam = new PagingParameter(this.supplierEventListOptions, this.afterDate, this.beforeDate);
                    const response = (await this.$supplierEventService.getSupplierEvents({
                        ...pagingParam,
                        supplierID: this.supplierID,
                    })) as SupplierEventResponse;
                    this.supplierEvents = response.supplierEvents;
                    this.totalSupplierEvents = response.totalSupplierEvents;
                }
            } catch (e) {
                this.errorMessage = "Failed to fetch supplier event data";
                this.showError = true;
            } finally {
                this.loading = false;
            }
        },
        handleChangeSupplierEventTableOptions(newOptions: DataTableOptions) {
            this.$emit("supplier-event-sort-updated", newOptions);
        },
        onListItemClicked(supplierEventID: string) {
            this.$emit("supplier-event-list-item-clicked", supplierEventID);
        },
    },
});
